// safety/incidents-and-injuries/components/tabs/WagesTab.tsx
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, money, todayISO } from '../../utils/incidents-utils';
import type { Incident, WageRecord } from '../../../../../types/incident.types';

export const WagesTab: React.FC<{
    incident: Incident;
    onAdd: (w: WageRecord) => void;
    onDelete: (id: string) => void;
}> = ({ incident: r, onAdd, onDelete }) => {
    const [modal, setModal] = useState(false);
    const paid = r.wages.reduce((a, x) => a + +x.gross, 0);
    const claimed = r.wages.reduce((a, x) => a + +x.claim, 0);
    const reimbursed = r.wages.reduce((a, x) => a + +x.reimbursed, 0);
    const out = claimed - reimbursed;

    return (
        <>
            <div className="grid grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">Total Wages Paid</div>
                    <div className="text-xl font-semibold mt-1">{money(paid)}</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                    <div className="text-xs text-muted-foreground">Total Claimed</div>
                    <div className="text-xl font-semibold mt-1 text-blue-600">{money(claimed)}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="text-xs text-muted-foreground">Total Reimbursed</div>
                    <div className="text-xl font-semibold mt-1 text-emerald-600">{money(reimbursed)}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                    <div className="text-xs text-muted-foreground">Outstanding</div>
                    <div className="text-xl font-semibold mt-1 text-amber-600">{money(out)}</div>
                </div>
            </div>

            <SectionTitle title="Weekly Records" right={<AddButton label="Add Week" onClick={() => setModal(true)} />} />
            <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                        <tr>
                            {['Week Ending', 'Gross Wage', 'Claim Amount', 'Reimbursed Amt', 'Received', 'Date Received', ''].map((h) => (
                                <th key={h} className="text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground px-3 py-2.5">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {r.wages.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No records yet.
                                </td>
                            </tr>
                        )}
                        {r.wages.map((x) => (
                            <tr key={x.id} className="border-b border-border last:border-0">
                                <td className="px-3 py-3 font-medium whitespace-nowrap">{fmtDate(x.weekEnding)}</td>
                                <td className="px-3 py-3">{money(x.gross)}</td>
                                <td className="px-3 py-3">{money(x.claim)}</td>
                                <td className="px-3 py-3">{money(x.reimbursed)}</td>
                                <td className="px-3 py-3">
                                    {x.received === 'Yes' ? (
                                        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">Yes</span>
                                    ) : (
                                        <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">Pending</span>
                                    )}
                                </td>
                                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{x.dateReceived ? fmtDate(x.dateReceived) : '–'}</td>
                                <td className="px-3 py-3 text-right">
                                    <button onClick={() => onDelete(x.id)} className="text-muted-foreground hover:text-destructive">
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <FormModal
                    title="Add Weekly Record"
                    fields={[
                        { key: 'weekEnding', label: 'Week Ending', type: 'date', value: todayISO() },
                        { key: 'gross', label: 'Gross Wage ($)', type: 'number', value: '0' },
                        { key: 'claim', label: 'Claim Amount ($)', type: 'number', value: '0' },
                        { key: 'reimbursed', label: 'Reimbursed Amt ($)', type: 'number', value: '0' },
                        { key: 'received', label: 'Received', type: 'select', options: ['Pending', 'Yes'], value: 'Pending' },
                        { key: 'dateReceived', label: 'Date Received', type: 'date', value: '' },
                    ]}
                    onClose={() => setModal(false)}
                    onSubmit={(v) => {
                        onAdd({ ...(v as any), gross: +v.gross, claim: +v.claim, reimbursed: +v.reimbursed });
                        setModal(false);
                    }}
                />
            )}
        </>
    );
};
